import { useCallback, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'

import { useEntitiesColors } from 'contexts/EntitiesColorsContext'
import { useSearchParams } from 'hooks'
import { useChartExport } from 'hooks/use-chart-export'
import type { TExportType } from 'hooks/use-chart-export/use-chart-export.types'
import { useTerritoryQueries } from 'hooks-queries/territory'
import { useTranslate } from 'lib/tolgee'
import { resolvePromotedNicheTargetName } from 'modules/niche/common/promoted-niche.utils'
import { getRelevanceInterval, getTimeVariables } from 'modules/territory/common/territory.utils'
import { analyticsTracker, getDatesInRange, TOLGEE_GLOBALS } from 'utils'
import { getExportOptions } from 'utils/chart-export'

import { GET_TERRITORIES_RELEVANCE_LOG, GET_TERRITORIES_VIEWS_LOG } from './niche-chart-selector.consts'
import { translations } from './niche-chart-selector.i18n'
import type {
  TChartTypes,
  INicheChartSelectorModelReturn,
  TLineChartTypes,
  INicheChartSelectorBaseProps,
} from './niche-chart-selector.types'
import { getBarChartData, getBaseTotalChartData, getDonutChartData } from './niche-chart-selector.utils'
import { getProviders } from '../common/niche.utils'

export const useNicheChartSelectorModel = ({
  nichesWithPerformance,
  chartDataUniverse,
  territoryIds,
  territoryContentFilters,
  isChartReady,
  chartDataLoading,
  chartDataError,
  chartDataRefetch,
  defaultUploadDate,
}: INicheChartSelectorBaseProps): INicheChartSelectorModelReturn => {
  const [selectedChartType, setSelectedChartType] = useState<TChartTypes>('bar')
  const [selectedLineChartType, setSelectedLineChartType] = useState<TLineChartTypes>('views')
  const shareChartRef = useRef(null)
  const barChartRef = useRef(null)
  const lineChartRef = useRef(null)
  const { getColorByEntityId } = useEntitiesColors()
  const t = useTranslate()
  const searchParams = useSearchParams()

  const currentChartRef = useMemo(() => {
    const chartToRef = {
      donut: shareChartRef,
      bar: barChartRef,
      line: lineChartRef,
    }

    return chartToRef[selectedChartType]
  }, [selectedChartType])

  const { exportChart } = useChartExport(currentChartRef)

  const interval = useMemo(() => {
    return getRelevanceInterval(searchParams.get('uploadDate') || defaultUploadDate || '')
  }, [searchParams, defaultUploadDate])

  const relevanceQueryVariables = useMemo(() => {
    const finalProviders = getProviders(searchParams)
    const uploadDate = searchParams.get('uploadDate') || defaultUploadDate
    const timeVariable = getTimeVariables(uploadDate)

    return {
      ids: territoryIds,
      territoryContentFilters,
      filters: {
        providers: finalProviders,
        interval: {
          timeUnit: interval,
        },
        ...timeVariable,
      },
    }
  }, [interval, searchParams, territoryIds, territoryContentFilters, defaultUploadDate])

  const viewsQueryVariables = useMemo(() => {
    const finalProviders = getProviders(searchParams)
    const uploadDate = searchParams.get('uploadDate') || defaultUploadDate
    const timeVariable = getTimeVariables(uploadDate)

    return {
      ids: territoryIds,
      territoryContentFilters,
      providers: finalProviders,
      interval: {
        timeUnit: interval,
      },
      ...timeVariable,
    }
  }, [interval, searchParams, territoryIds, territoryContentFilters, defaultUploadDate])

  const {
    data: relevanceData,
    loading: relevanceLoading,
    error: relevanceError,
    refetch: relevanceRefetch,
  } = useTerritoryQueries({
    errorLog: GET_TERRITORIES_RELEVANCE_LOG,
    queryName: 'getTerritoriesRelevance',
    queryVariables: relevanceQueryVariables,
    options: {
      skip: !isChartReady,
    },
  })

  const {
    data: viewsData,
    loading: viewsLoading,
    error: viewsError,
    refetch: viewsRefetch,
  } = useTerritoryQueries({
    errorLog: GET_TERRITORIES_VIEWS_LOG,
    queryName: 'getTerritoriesViewsOverTime',
    queryVariables: viewsQueryVariables,
    options: {
      skip: !isChartReady,
    },
  })

  const selectChartType = (chartType: TChartTypes) => {
    const { NICHE_TAB } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(NICHE_TAB.CHANGE_CHART_TYPE, {
      chartType,
    })
    setSelectedChartType(chartType)
  }

  const handlePrintChart = useCallback(
    (exportType: TExportType = 'image/png', chartTitle?: string) => exportChart(exportType, chartTitle),
    [exportChart]
  )

  const menuItems = useMemo(() => {
    const handleChangeType = (type: TLineChartTypes) => {
      const { NICHE_TAB } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(NICHE_TAB.CHANGE_CHART_METRIC, {
        metric: type,
      })
      setSelectedLineChartType(type)
    }
    return [
      {
        label: t(TOLGEE_GLOBALS.views),
        onClick: () => handleChangeType('views'),
        isSelected: selectedLineChartType === 'views',
      },
      {
        label: t(TOLGEE_GLOBALS.relevance),
        onClick: () => handleChangeType('relevance'),
        isSelected: selectedLineChartType === 'relevance',
      },
    ]
  }, [selectedLineChartType, t])

  const shouldDisplayChartDropdown = selectedChartType === 'line'

  const title = useMemo(() => {
    if (selectedChartType === 'bar') {
      return t(TOLGEE_GLOBALS.performance)
    }
    if (selectedChartType === 'donut') {
      return t(translations.shareByThemes)
    }
    if (selectedLineChartType === 'relevance') {
      return t(TOLGEE_GLOBALS.relevanceOverTime)
    }
    return t(TOLGEE_GLOBALS.viewsOverTime)
  }, [selectedChartType, selectedLineChartType, t])

  const engagementShareChartData = useMemo(() => {
    return getBaseTotalChartData(nichesWithPerformance, 'totalEngagement', getColorByEntityId)
  }, [getColorByEntityId, nichesWithPerformance])

  const viewsShareChartData = useMemo(() => {
    return getBaseTotalChartData(nichesWithPerformance, 'totalViews', getColorByEntityId)
  }, [getColorByEntityId, nichesWithPerformance])

  const postsShareChartData = useMemo(() => {
    return getBaseTotalChartData(nichesWithPerformance, 'totalPosts', getColorByEntityId)
  }, [getColorByEntityId, nichesWithPerformance])

  const shareChartData = useMemo(() => {
    return getDonutChartData(engagementShareChartData, viewsShareChartData, postsShareChartData, t)
  }, [engagementShareChartData, postsShareChartData, t, viewsShareChartData])

  const barChartData = useMemo(() => {
    return getBarChartData(engagementShareChartData, viewsShareChartData, t)
  }, [engagementShareChartData, t, viewsShareChartData])

  const relevanceChartData = useMemo(() => {
    const territoriesData = relevanceData?.listTerritories || []
    return territoriesData.map(item => {
      return {
        name: resolvePromotedNicheTargetName(item) || item.name,
        color: getColorByEntityId(item.territoryId, 'topics'),
        series: item.territoryPerformance?.relevancev2?.sparkline || [],
      }
    })
  }, [relevanceData?.listTerritories, getColorByEntityId])

  const viewsChartData = useMemo(() => {
    const territoriesData = viewsData?.listTerritories || []
    return territoriesData.map(territory => {
      const metricsPerMonth = territory?.territoryPerformance?.metricsPerMonth || []
      const views = metricsPerMonth.map(item => Number(item.totalViews))
      return {
        name: resolvePromotedNicheTargetName(territory) || territory.name,
        color: getColorByEntityId(territory.territoryId, 'topics'),
        series: views,
      }
    })
  }, [getColorByEntityId, viewsData?.listTerritories])

  const lineChartData = useMemo(() => {
    if (selectedLineChartType === 'views') {
      return viewsChartData
    }
    return relevanceChartData
  }, [relevanceChartData, selectedLineChartType, viewsChartData])

  const lineChartDataLabels = useMemo(() => {
    if (selectedLineChartType === 'views') {
      const territoriesData = viewsData?.listTerritories || []
      const firstItem = territoriesData?.[0]
      const dates = firstItem?.territoryPerformance?.metricsPerMonth?.map(item => {
        const format = interval === 'month' ? 'MMM/YYYY' : 'MMM DD, YYYY'
        return dayjs(item?.date).format(format)
      })
      return dates
    }
    const relevance = relevanceData?.listTerritories?.[0]?.territoryPerformance?.relevancev2
    const dates = getDatesInRange(
      relevance?.startDate,
      relevance?.endDate,
      interval === 'month' ? 'MMM/YYYY' : 'MMM DD, YYYY',
      interval
    )
    return dates
  }, [selectedLineChartType, relevanceData?.listTerritories, interval, viewsData?.listTerritories])

  const chartExportOptions = useMemo(() => {
    return getExportOptions(title, chartDataUniverse)
  }, [chartDataUniverse, title])

  const chartKey = useMemo(() => {
    return nichesWithPerformance.map(item => item.id).join('-')
  }, [nichesWithPerformance])

  const loading = useMemo(() => {
    if (selectedChartType !== 'line') {
      return chartDataLoading
    }
    if (selectedLineChartType === 'relevance') {
      return relevanceLoading
    }
    return viewsLoading
  }, [chartDataLoading, relevanceLoading, selectedChartType, selectedLineChartType, viewsLoading])

  const error = useMemo(() => {
    if (selectedChartType !== 'line') {
      return chartDataError
    }
    if (selectedLineChartType === 'relevance') {
      return Boolean(relevanceError)
    }
    return Boolean(viewsError)
  }, [chartDataError, relevanceError, selectedChartType, selectedLineChartType, viewsError])

  const handleRefetch = useCallback(() => {
    if (selectedChartType !== 'line') {
      chartDataRefetch()
      return
    }
    if (selectedLineChartType === 'relevance') {
      relevanceRefetch()
      return
    }
    viewsRefetch()
  }, [chartDataRefetch, relevanceRefetch, selectedChartType, selectedLineChartType, viewsRefetch])

  const shouldDisableButtons = useMemo(() => {
    return !isChartReady || loading || error
  }, [error, isChartReady, loading])

  return {
    selectedChartType,
    selectChartType,
    chartOptions: menuItems,
    shouldDisplayChartDropdown,
    title,
    shareChartData,
    chartKey,
    shareChartRef,
    handlePrintChart,
    chartExportOptions,
    barChartData,
    barChartRef,
    lineChartRef,
    lineChartData,
    lineChartDataLabels,
    loading,
    error: Boolean(error),
    refetch: handleRefetch,
    shouldDisableButtons,
  }
}
